/**
 * ReminderSettingsPanel — toggle reminders on/off per parameter (bonus C).
 */
import { useMemo } from 'react';
import { ALL_TEST_KITS } from '@reefapp/shared';
import type { ParameterKey } from '@reefapp/shared';
import type { ReminderSettings } from './useReminderSettings';

export interface ReminderSettingsPanelProps {
  settings: ReminderSettings;
  onSetEnabled(key: ParameterKey, enabled: boolean): void;
  onSetThresholdDays(key: ParameterKey, days: number): void;
}

export function ReminderSettingsPanel({
  settings,
  onSetEnabled,
  onSetThresholdDays,
}: ReminderSettingsPanelProps) {
  const parameters = useMemo(() => {
    const seen = new Set<ParameterKey>();
    const result: ParameterKey[] = [];
    for (const kit of ALL_TEST_KITS) {
      if (!seen.has(kit.parameterKey)) {
        seen.add(kit.parameterKey);
        result.push(kit.parameterKey);
      }
    }
    return result.sort();
  }, []);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-cyan-400 mb-3">Test Reminders</h3>
      <div className="space-y-2">
        {parameters.map((key) => {
          const setting = settings[key];
          const enabled = setting?.enabled ?? false;
          const thresholdDays = setting?.thresholdDays ?? 7;
          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <label className="flex items-center gap-2 text-slate-300 min-w-0">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => onSetEnabled(key, e.target.checked)}
                  className="rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500 shrink-0"
                />
                <span className="capitalize truncate">{key}</span>
              </label>
              {enabled && (
                <select
                  value={thresholdDays}
                  onChange={(e) =>
                    onSetThresholdDays(key, Number(e.target.value))
                  }
                  className="rounded border-slate-600 bg-slate-700 text-slate-200 text-xs px-2 py-1 focus:border-cyan-500 shrink-0"
                >
                  {[1, 3, 5, 7, 10, 14, 21, 30, 60].map((d) => (
                    <option key={d} value={d}>
                      {d} days
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
